import React, { useState } from 'react';

import { ArrowLeft, Eye, Percent, RefreshCw } from 'lucide-react';

import { useTypedSelector } from '../../store';
import { formatCurrency, formatRatePlanType } from '../../utils/utils';
import { Alert, Button } from '../common';
import CustomizeFeeStructureModal from '../CustomizeFeeStructureModal';
import ExportButton from '../ExportButton';

import RecalculationInfo from './RecalculationInfo';
import RevenueAnalysisSummary from './RevenueAnalysisSummary';
import ViewSwitcher from './ViewSwitcher';

interface FeeStructureData {
	visaMastercardDiscoverCredit: number;
	visaMastercardDiscoverDebit: number;
	americanExpress: number;
	pinDebit: number;
	creditTransactions: number;
	debitTransactions: number;
	pinDebitTransactions: number;
	assessmentFees: boolean;
	amexRate: boolean;
}

interface AnalysisHeaderProps {
	/** ROC-72: hide savings, export and quote controls for a file that is not a statement. */
	hideAnalysis?: boolean;
	document: {
		id: string;
		name: string;
		date: string;
		status: string;
	};
	onBack: () => void;
	onToggleDocument: () => void;
	isDocumentVisible: boolean;
	onboardingContext: boolean;
	analysisData?: {
		processingMonth: string;
		duesFeesAssessments: any[];
		interchange: any[];
		authorizationAndDiscount: any[];
		otherFees: any[];
		processorName: string;
		processorNamePlus: string;
		totals: {
			ic: number;
			icPlus: number;
		};
		revenue_analysis?: any;
	};
	monthlySavingsData?: {
		value: number;
		isNegative: boolean;
	};
	// Backend-calculated totals (statement-type-aware)
	calculatedTotals?: {
		ic: number;
		icPlus: number;
		savings: {
			monthly: number;
			yearly: number;
			percent_lower: number;
			is_negative: boolean;
		} | null;
		effectiveRates: {
			current: number;
			comparison: number;
		} | null;
		volume: number | null;
		calculationMethod: string | null;
		totalSource?: 'document' | 'calculated';
		documentTotal?: {
			amount: number;
			label: string;
			page_number?: number;
		};
	};
	analysisType?: string;
	actionType?: string; // Action type (upload, rerun, recalculation)
	logsData?: any; // For progress calculation
	onCustomizeFeeStructure?: (feeData: FeeStructureData) => void;
	onRefetchData?: () => void;
	isLoading?: boolean;
	isFetching?: boolean;
	customRates?: any; // Custom rates from processing_metadata
	onRerun?: () => void;
	isRerunning?: boolean;
	revenueAnalysis?: RevenueAnalysis; // ISO profit - reactive to line item edits
	viewMode?: 'default' | 'user_edits' | 'customized_quote';
	onViewModeChange?: (
		view: 'default' | 'user_edits' | 'customized_quote',
	) => void;
	hasUserEdits?: boolean;
	hasCustomizedQuote?: boolean;
	// Statement-type mismatch banner (ROC-62): present only when the
	// classifier-detected type differs from the user-selected one
	typeMismatch?: {
		detectedRatePlanType: string;
		selectedRatePlanType: string;
		onRecalculateAsDetected: () => void;
		onDismiss: () => void;
		isRecalculating?: boolean;
	};
}

const AnalysisHeader: React.FC<AnalysisHeaderProps> = ({
	document,
	onBack,
	onToggleDocument,
	isDocumentVisible,
	onboardingContext,
	analysisData,
	monthlySavingsData,
	calculatedTotals,
	analysisType,
	actionType,
	logsData,
	onCustomizeFeeStructure,
	onRefetchData,
	isLoading,
	isFetching,
	customRates,
	onRerun,
	isRerunning,
	revenueAnalysis,
	viewMode = 'default',
	onViewModeChange,
	hasUserEdits = false,
	hasCustomizedQuote = false,
	typeMismatch,
	hideAnalysis = false,
}) => {
	const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
	const showAnalysis = document.status === 'completed' && !hideAnalysis;

	// Get tenant from Redux state for dynamic branding
	const tenant = useTypedSelector((state) => state.tenant.tenant);

	// Determine the company name to display
	// Priority: tenant name > "RocketFee" (default for main domains)
	const companyName = tenant?.name || 'RocketFee';

	const handleCustomizeQuote = () => {
		setIsCustomizeModalOpen(true);
	};

	const handleCloseFeeModal = () => {
		setIsCustomizeModalOpen(false);
	};

	const handleConfirmFeeStructure = (feeData: FeeStructureData) => {
		if (onCustomizeFeeStructure) {
			onCustomizeFeeStructure(feeData);
		}

		// Refetch data after customization
		if (onRefetchData) {
			onRefetchData();
		}

		setIsCustomizeModalOpen(false);
	};

	const getStatusBadge = (status: string) => {
		const baseClasses = 'px-3 py-1 rounded-full text-xs font-semibold';
		const statusClasses = {
			processing: 'bg-yellow-500 text-yellow-900',
			completed: 'bg-green-500 text-green-900',
			failed: 'bg-red-500 text-red-900',
		};
		return `${baseClasses} ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-500 text-gray-900'}`;
	};

	return (
		<div className="w-full bg-gray-50">
			{/* Document Header Section */}
			<div className="w-full px-6 py-4 border-b border-gray-200">
				<div className="w-full flex justify-between items-center gap-4">
					{/* Left Side - Back Button and Document Name */}
					<div className="flex items-center gap-4">
						{!onboardingContext && (
							<Button
								onClick={onBack}
								className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-primary-600 transition-colors border border-gray-200 rounded-lg"
								icon={ArrowLeft}
								variant="outline"
								size="sm"
							>
								Back
							</Button>
						)}
						<div className="flex flex-col gap-1">
							<div className="text-lg font-semibold text-gray-900">
								{document.name}
							</div>
							<RecalculationInfo
								logsData={logsData}
								customRates={customRates}
								analysisType={analysisType}
								actionType={actionType}
							/>
						</div>
					</div>

					{/* Right Side - Action Buttons */}
					<div className="flex items-center gap-3">
						{/* Revenue Analysis Summary - prefer reactive revenueAnalysis from line items API */}
						{(revenueAnalysis || analysisData?.revenue_analysis) && (
							<RevenueAnalysisSummary
								revenueAnalysis={
									revenueAnalysis || analysisData?.revenue_analysis
								}
								analysisType={analysisType}
							/>
						)}

						{onRerun && (
							<Button
								onClick={onRerun}
								className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
								icon={RefreshCw}
								variant="outline"
								size="sm"
								loading={isRerunning}
								disabled={isRerunning}
							>
								Rerun Analysis
							</Button>
						)}
						<Button
							onClick={onToggleDocument}
							className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
							icon={Eye}
							variant="outline"
							size="sm"
						>
							View Statement
						</Button>
						{analysisData && showAnalysis && (
							<ExportButton
								analysisData={analysisData}
								fileId={document.id}
								viewMode={viewMode}
							/>
						)}
						{showAnalysis && (
							<Button
								onClick={handleCustomizeQuote}
								className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
								size="sm"
								icon={Percent}
								variant="outline"
							>
								Customize Quote
							</Button>
						)}
						{/* View Switcher */}
						{showAnalysis && onViewModeChange && (
							<ViewSwitcher
								currentView={viewMode}
								onViewChange={onViewModeChange}
								hasUserEdits={hasUserEdits || false}
								hasCustomizedQuote={hasCustomizedQuote || false}
							/>
						)}
					</div>
				</div>

				{/* Summary Cards - Professional Comparison View */}
				{showAnalysis && (
					<div className="mt-6 grid grid-cols-3 gap-5">
						{/* Current Processor Card */}
						<div className="relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-5 shadow-lg border border-slate-300">
							<div className="absolute top-0 right-0 w-24 h-24 bg-slate-300/30 rounded-full -mr-12 -mt-12" />
							<div className="relative">
								<div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3 flex items-center gap-2">
									<div className="w-1 h-3 bg-red-500 rounded-full" />
									Current Processor
								</div>
								{!analysisData || isLoading || isFetching ? (
									<>
										<div className="h-12 bg-slate-300/50 rounded mb-3 animate-pulse" />
										<div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent my-3" />
										<div className="h-12 bg-slate-300/50 rounded animate-pulse" />
									</>
								) : (
									<>
										<div className="mb-4">
											<div className="text-3xl font-bold text-slate-800 mb-1">
												{(() => {
													// Prefer backend-calculated effective rate
													if (calculatedTotals?.effectiveRates) {
														return `${calculatedTotals.effectiveRates.current.toFixed(2)}%`;
													}
													// Fallback to frontend calculation
													const calculateTotalVolume = (
														data: any[],
													): number => {
														return data.reduce((sum, item) => {
															const icVolume = parseFloat(
																item.ic?.volume
																	?.toString()
																	.replace(/[$,]/g, '') || '0',
															);
															const childrenVolume = (
																item.children || []
															).reduce((childSum: number, child: any) => {
																return (
																	childSum +
																	parseFloat(
																		child.ic?.volume
																			?.toString()
																			.replace(/[$,]/g, '') || '0',
																	)
																);
															}, 0);
															return sum + icVolume + childrenVolume;
														}, 0);
													};
													const totalVolume =
														calculateTotalVolume(
															analysisData.duesFeesAssessments,
														) +
														calculateTotalVolume(analysisData.interchange) +
														calculateTotalVolume(
															analysisData.authorizationAndDiscount,
														);
													const effectiveRate =
														totalVolume > 0
															? (analysisData.totals.ic / totalVolume) * 100
															: 0;
													return `${effectiveRate.toFixed(2)}%`;
												})()}
											</div>
											<div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
												Rate
											</div>
										</div>
										<div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent my-3" />
										<div>
											<div className="text-2xl font-bold text-slate-800 mb-1">
												{formatCurrency(
													calculatedTotals?.ic ?? analysisData.totals.ic,
												)}
											</div>
											<div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
												Monthly Fees
											</div>
											{calculatedTotals?.volume != null && (
												<div className="mt-2 text-xs text-slate-600">
													on {formatCurrency(calculatedTotals.volume)} monthly
													card volume
												</div>
											)}
										</div>
									</>
								)}
							</div>
						</div>

						{/* With [Company Name] Card */}
						<div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 shadow-lg border border-blue-200">
							<div className="absolute top-0 right-0 w-24 h-24 bg-blue-200/30 rounded-full -mr-12 -mt-12" />
							<div className="relative">
								<div className="text-[10px] font-bold text-blue-700 uppercase tracking-widest mb-3 flex items-center gap-2">
									<div className="w-1 h-3 bg-blue-500 rounded-full" />
									With {companyName}
								</div>
								{!analysisData || isLoading || isFetching ? (
									<>
										<div className="h-12 bg-blue-200/50 rounded mb-3 animate-pulse" />
										<div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent my-3" />
										<div className="h-12 bg-blue-200/50 rounded animate-pulse" />
									</>
								) : (
									<>
										<div className="mb-4">
											<div className="text-3xl font-bold text-blue-900 mb-1">
												{(() => {
													// Prefer backend-calculated effective rate
													if (calculatedTotals?.effectiveRates) {
														return `${calculatedTotals.effectiveRates.comparison.toFixed(2)}%`;
													}
													// Fallback to frontend calculation
													const calculateTotalVolume = (
														data: any[],
													): number => {
														return data.reduce((sum, item) => {
															const icVolume = parseFloat(
																item.ic?.volume
																	?.toString()
																	.replace(/[$,]/g, '') || '0',
															);
															const childrenVolume = (
																item.children || []
															).reduce((childSum: number, child: any) => {
																return (
																	childSum +
																	parseFloat(
																		child.ic?.volume
																			?.toString()
																			.replace(/[$,]/g, '') || '0',
																	)
																);
															}, 0);
															return sum + icVolume + childrenVolume;
														}, 0);
													};
													const totalVolume =
														calculateTotalVolume(
															analysisData.duesFeesAssessments,
														) +
														calculateTotalVolume(analysisData.interchange) +
														calculateTotalVolume(
															analysisData.authorizationAndDiscount,
														);
													const effectiveRate =
														totalVolume > 0
															? (analysisData.totals.icPlus / totalVolume) * 100
															: 0;
													return `${effectiveRate.toFixed(2)}%`;
												})()}
											</div>
											<div className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">
												Rate
											</div>
										</div>
										<div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent my-3" />
										<div>
											<div className="text-2xl font-bold text-blue-900 mb-1">
												{formatCurrency(
													calculatedTotals?.icPlus ??
														analysisData.totals.icPlus,
												)}
											</div>
											<div className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">
												Monthly Fees
											</div>
										</div>
									</>
								)}
							</div>
						</div>

						{/* Your Savings Card */}
						<div
							className={`relative overflow-hidden rounded-xl p-5 shadow-lg ${
								!monthlySavingsData
									? 'bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300'
									: monthlySavingsData.isNegative
										? 'bg-gradient-to-br from-red-50 to-red-100 border border-red-200'
										: 'bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200'
							}`}
						>
							<div
								className={`absolute top-0 right-0 w-24 h-24 rounded-full -mr-12 -mt-12 ${
									monthlySavingsData?.isNegative
										? 'bg-red-200/30'
										: 'bg-emerald-200/30'
								}`}
							/>
							<div className="relative">
								<div
									className={`text-[10px] font-bold uppercase tracking-widest mb-3 flex items-center gap-2 ${
										monthlySavingsData?.isNegative
											? 'text-red-700'
											: 'text-emerald-700'
									}`}
								>
									<div
										className={`w-1 h-3 rounded-full ${monthlySavingsData?.isNegative ? 'bg-red-500' : 'bg-emerald-500'}`}
									/>
									Your Savings
								</div>
								{!monthlySavingsData || isLoading || isFetching ? (
									<>
										<div className="h-12 bg-emerald-200/50 rounded mb-3 animate-pulse" />
										<div className="h-px bg-gradient-to-r from-transparent via-emerald-300 to-transparent my-3" />
										<div className="h-12 bg-emerald-200/50 rounded animate-pulse" />
									</>
								) : (
									<>
										<div className="mb-4">
											<div
												className={`text-3xl font-bold mb-1 ${
													monthlySavingsData.isNegative
														? 'text-red-900'
														: 'text-emerald-900'
												}`}
											>
												{monthlySavingsData.isNegative ? '' : '+ '}
												{formatCurrency(monthlySavingsData.value)}
											</div>
											<div
												className={`text-[10px] font-medium uppercase tracking-wider ${
													monthlySavingsData.isNegative
														? 'text-red-600'
														: 'text-emerald-600'
												}`}
											>
												Monthly (
												{(() => {
													// Prefer backend-calculated percent savings
													const percentSavings = calculatedTotals?.savings
														? Math.abs(calculatedTotals.savings.percent_lower)
														: analysisData && analysisData.totals.ic > 0
															? Math.abs(
																	(monthlySavingsData.value /
																		analysisData.totals.ic) *
																		100,
																)
															: 0;
													return `${percentSavings.toFixed(0)}% ${monthlySavingsData.isNegative ? 'Higher' : 'Lower'}`;
												})()}
												)
											</div>
										</div>
										<div
											className={`h-px bg-gradient-to-r from-transparent to-transparent my-3 ${
												monthlySavingsData.isNegative
													? 'via-red-300'
													: 'via-emerald-300'
											}`}
										/>
										<div>
											<div
												className={`text-2xl font-bold mb-1 ${
													monthlySavingsData.isNegative
														? 'text-red-900'
														: 'text-emerald-900'
												}`}
											>
												{monthlySavingsData.isNegative ? '' : '+ '}
												{formatCurrency(
													calculatedTotals?.savings?.yearly ??
														monthlySavingsData.value * 12,
												)}
											</div>
											<div
												className={`text-[10px] font-medium uppercase tracking-wider ${
													monthlySavingsData.isNegative
														? 'text-red-600'
														: 'text-emerald-600'
												}`}
											>
												Yearly
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Statement-type mismatch banner (ROC-62) */}
				{showAnalysis && typeMismatch && (
					<div className="mt-4">
						<Alert
							variant="warning"
							title="Statement type mismatch"
							dismissible
							onDismiss={typeMismatch.onDismiss}
						>
							<p>
								This looks like{' '}
								{/^[aeiou]/i.test(
									formatRatePlanType(typeMismatch.detectedRatePlanType),
								)
									? 'an'
									: 'a'}{' '}
								<span className="font-semibold">
									{formatRatePlanType(typeMismatch.detectedRatePlanType)}
								</span>{' '}
								statement, but it was analyzed as{' '}
								<span className="font-semibold">
									{formatRatePlanType(typeMismatch.selectedRatePlanType)}
								</span>
								. Do you want to process it as{' '}
								{formatRatePlanType(typeMismatch.detectedRatePlanType)}?
							</p>
							<div className="mt-3 flex items-center gap-3">
								<Button
									size="sm"
									variant="warning"
									onClick={typeMismatch.onRecalculateAsDetected}
									disabled={typeMismatch.isRecalculating}
								>
									{typeMismatch.isRecalculating
										? 'Recalculating…'
										: `Process as ${formatRatePlanType(typeMismatch.detectedRatePlanType)}`}
								</Button>
								<Button
									size="sm"
									variant="ghost"
									onClick={typeMismatch.onDismiss}
									disabled={typeMismatch.isRecalculating}
								>
									Keep my selection
								</Button>
							</div>
						</Alert>
					</div>
				)}
			</div>

			{/* Customize Fee Structure Modal */}
			{isCustomizeModalOpen && customRates && (
				<CustomizeFeeStructureModal
					isOpen={isCustomizeModalOpen}
					onClose={handleCloseFeeModal}
					onConfirm={handleConfirmFeeStructure}
					fileId={document.id}
					context="analysis"
					customRatesData={customRates}
				/>
			)}
		</div>
	);
};

export default AnalysisHeader;
